import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl, FormattedMessage } from 'react-intl';
import { fromJS } from 'immutable';
import { styled } from '@mui/system';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import messages from './messagesExchangeOrder';

import { formatPrice } from '../../../utils';

import styles from './styles';

const StyledAppBar = styled(AppBar)({
	...styles.headerSelectedAppBar,
});

const StyledToolbar = styled(Toolbar)({
	...styles.toolbar,
});

const StyledTable = styled('table')({
	...styles.table,
});

const StyledHeaderSelectedLabel = styled(Typography)({
	...styles.headerSelectedLabel,
});

const StyledPassengers = styled(Typography)({
	...styles.passengers,
});

const StyledHeaderTable = styled(Table)({
	...styles.headerTable,
});

const StyledHeaderTableSelectedRow = styled(TableRow)({
	...styles.headerSelectedRow,
});

const ExchangeOrderTitle = ({
	intl,
	intl: { formatMessage },
	exchangeableTicketableFareIds,
	orderDetails,
	isSeasonTrip,
	seasonFareDetails,
	ticketableFareGroups,
	showSeatsAndServices,
	selectedSeat,
	fareSelected,
	style,
}) => {
	const immutableTicketableFareGroups = ticketableFareGroups?.length
		? fromJS(ticketableFareGroups)
		: orderDetails.getIn(['BookingOrders', 0, 'ticketableFareGroups']);

	const selectedTicketableFareGroup = immutableTicketableFareGroups.filter(
		(ticketableFareGroup) =>
			exchangeableTicketableFareIds.includes(
				ticketableFareGroup.get('BookingDetailsLegTicketableFareIds'),
			),
	);

	const newSeatReservation =
		fareSelected && fareSelected.toJS()?.[0]?.leg?.ShoppingLegSolutions[0];

	return (
		<StyledAppBar position="static" color="default" style={{ ...style }}>
			<StyledToolbar variant="dense">
				<StyledTable>
					<tbody>
						<tr>
							<th>
								<StyledHeaderSelectedLabel variant="body1">
									<FormattedMessage {...messages.lblPassengers} />
								</StyledHeaderSelectedLabel>
							</th>
							<th>
								<StyledPassengers variant="caption">
									{selectedTicketableFareGroup
										.getIn([0, 'passengers'])
										?.toJS()
										.sort((a, b) => (a.passengerId > b.passengerId ? 1 : -1))
										.map(
											(passenger) =>
												`${passenger.firstName} ${passenger.lastName}`,
										)
										.join(', ')}
								</StyledPassengers>
							</th>
						</tr>
						<tr>
							<th>
								<StyledHeaderSelectedLabel variant="body1">
									<FormattedMessage {...messages.lblExchangedHeader} />
								</StyledHeaderSelectedLabel>
							</th>
							<th>
								<StyledHeaderTable>
									<TableHead>
										<StyledHeaderTableSelectedRow>
											<TableCell data-testid="origin">
												{formatMessage(
													isSeasonTrip
														? messages.lblOrigin
														: messages.lblDepartStation,
												)}
											</TableCell>
											<TableCell data-testid="destination">
												{formatMessage(
													isSeasonTrip
														? messages.lblDestination
														: messages.lblDepartDate,
												)}
											</TableCell>
											<TableCell data-testid="start-date">
												{formatMessage(
													isSeasonTrip
														? messages.lblSeasonStartDate
														: messages.lblArriveStation,
												)}
											</TableCell>
											<TableCell data-testid="end-date">
												{formatMessage(
													isSeasonTrip
														? messages.lblSeasonEndDate
														: messages.lblArriveDate,
												)}
											</TableCell>
											<TableCell>
												<FormattedMessage {...messages.lblPrice} />
											</TableCell>
											{showSeatsAndServices && (
												<>
													<TableCell>Seats</TableCell>
													<TableCell>Amenities & Services</TableCell>
												</>
											)}
										</StyledHeaderTableSelectedRow>
									</TableHead>
									<TableBody>
										{!newSeatReservation &&
											selectedTicketableFareGroup.map((selectedFare) => {
												const ticketableFareID = selectedFare.get(
													'BookingDetailsLegTicketableFareIds',
												);
												const returnDepartDate =
													selectedFare.get('returnDepartDate');
												const returnArriveDate =
													selectedFare.get('returnArriveDate');

												const fareDetails = isSeasonTrip
													? {
															departStation:
																seasonFareDetails.get('departStationName'),
															departDate:
																seasonFareDetails.get('arriveStationName'),
															arriveStation: seasonFareDetails.get('startDate'),
															arriveDate: seasonFareDetails.get('endDate'),
														}
													: {
															departStation: selectedFare.get(
																'departStationLongName',
															),
															arriveStation: selectedFare.get(
																'arriveStationLongName',
															),
															departDate: selectedFare.get('departDate'),
															arriveDate: selectedFare.get('arriveDate'),
														};

												const rows = [
													<StyledHeaderTableSelectedRow key={ticketableFareID}>
														<TableCell data-testid="selected-fare-depart-station-name">
															{fareDetails.departStation}
														</TableCell>
														<TableCell data-testid="selected-fare-arrive-station-name">
															{fareDetails.departDate}
														</TableCell>
														<TableCell data-testid="selected-fare-start-date">
															{fareDetails.arriveStation}
														</TableCell>
														<TableCell data-testid="selected-fare-end-date">
															{fareDetails.arriveDate}
														</TableCell>
														<TableCell>
															{formatPrice(
																selectedFare.get('ticketableFarePrice'),
																selectedFare.get('ticketableFareCurrency'),
																intl,
															)}
														</TableCell>
														{showSeatsAndServices && (
															<>
																<TableCell>
																	{/* eslint-disable-next-line no-unneeded-ternary */}
																	{selectedSeat
																		? selectedSeat
																		: selectedFare
																				.get('BookingLegTravelSegments')
																				.toJS()[0].BookingLegTravelSegmentSeats}
																</TableCell>
																<TableCell>
																	{selectedFare
																		.get('BookingLegTravelSegments')
																		.toJS()[0]
																		.BookingOrderServicesAndAmenities.map(
																			(item) => (
																				<span
																					key={
																						item.BookingOrderServicesAndAmenitiesDesc
																					}
																				>
																					{
																						item.BookingOrderServicesAndAmenitiesDesc
																					}
																					<br />
																				</span>
																			),
																		)}
																</TableCell>
															</>
														)}
													</StyledHeaderTableSelectedRow>,
												];

												// A single ticketable fare id can represent both legs of a return fare — show it as a
												// second row. The return leg reverses the outbound stations; its price shows as £0.00
												if (!isSeasonTrip && returnDepartDate) {
													rows.push(
														<StyledHeaderTableSelectedRow
															key={`${ticketableFareID}-return`}
														>
															<TableCell data-testid="selected-fare-return-depart-station-name">
																{selectedFare.get('arriveStationLongName')}
															</TableCell>
															<TableCell data-testid="selected-fare-return-depart-date">
																{returnDepartDate}
															</TableCell>
															<TableCell data-testid="selected-fare-return-arrive-station-name">
																{selectedFare.get('departStationLongName')}
															</TableCell>
															<TableCell data-testid="selected-fare-return-arrive-date">
																{returnArriveDate}
															</TableCell>
															<TableCell data-testid="selected-fare-return-price">
																{formatPrice(
																	0,
																	selectedFare.get('ticketableFareCurrency'),
																	intl,
																)}
															</TableCell>
															{showSeatsAndServices && (
																<>
																	<TableCell />
																	<TableCell />
																</>
															)}
														</StyledHeaderTableSelectedRow>,
													);
												}

												return rows;
											})}

										{/* New Seat Reservations do not have access to ticketableFareGroup until the order has been made. We have to use local data instead- */}
										{newSeatReservation && (
											<StyledHeaderTableSelectedRow>
												<TableCell data-testid="selected-fare-depart-station-name">
													{
														newSeatReservation?.ShoppingLegSolutionDepartStationName
													}
												</TableCell>
												<TableCell data-testid="selected-fare-start-date">
													{
														newSeatReservation?.ShoppingLegSolutionDepartDateTime
													}
												</TableCell>
												<TableCell data-testid="selected-fare-arrive-station-name">
													{
														newSeatReservation?.ShoppingLegSolutionArriveStationName
													}
												</TableCell>

												<TableCell data-testid="selected-fare-end-date">
													{
														newSeatReservation?.ShoppingLegSolutionArriveDateTime
													}
												</TableCell>
												<TableCell>
													{formatPrice(
														newSeatReservation?.ShoppingLegFares[0]
															?.ShoppingLegFareTotalPrice,
														newSeatReservation?.ShoppingLegFares[0]
															?.ShoppingLegFareTotalPriceCurrency,
														intl,
													)}
												</TableCell>
												{showSeatsAndServices && (
													<TableCell>
														{selectedSeat || (
															<FormattedMessage
																{...messages.lblNoSpecificSeat}
															/>
														)}
													</TableCell>
												)}
											</StyledHeaderTableSelectedRow>
										)}
									</TableBody>
								</StyledHeaderTable>
							</th>
						</tr>
					</tbody>
				</StyledTable>
			</StyledToolbar>
		</StyledAppBar>
	);
};

ExchangeOrderTitle.propTypes = {
	intl: PropTypes.object,
	exchangeableTicketableFareIds: PropTypes.array,
	orderDetails: PropTypes.object,
	isSeasonTrip: PropTypes.bool,
	seasonFareDetails: PropTypes.object,
	ticketableFareGroups: PropTypes.array,
	showSeatsAndServices: PropTypes.bool,
	selectedSeat: PropTypes.string,
	fareSelected: PropTypes.object,
	style: PropTypes.object,
};

export { ExchangeOrderTitle as ExchangeOrderTitleAlias };

export default injectIntl(ExchangeOrderTitle);
